import { useMemo } from 'react'

import type { Filter } from '@cubejs-client/core'
import { CubeOperator, resultSetToTableData } from 'cube'
import { DRIFT_CHAT_OPPORTUNITY_OVERVIEW } from 'cube/constants'
import { differenceInDays, isEqual } from 'date-fns'

import {
  type CubeQuery,
  CubeQueryBuilder,
  useCubeQuery,
} from '@driftt/dds.reports'

import { resolveCubeKPIResults } from 'components/KPICard'
import {
  OpportunitiesTableRow,
  OpportunityCountBaseQuery,
} from 'layouts/RevenueReport'
import { StageStatus } from 'layouts/RevenueReport/Opportunities/Opportunities.types'
import { useOverviewStore } from 'layouts/RevenueReport/Overview/Overview.context'
import { getInfluencedAndSourcedFilters } from 'layouts/RevenueReport/RevenueKPICards/RevenueKPICards.helpers'

export interface IInfluencedSalesCycle {
  averageDaysInCycleOfInfluencedOpportunities: number
  percentageResult: number
  isLoading: boolean
  hasNoData: boolean
  hasError: boolean
  salesCycleInfluencedCubeQuery: CubeQuery
}

const reduceAverageDaysInCycle = (
  acc,
  { opportunityClosedAt = '', opportunityCreatedAt = '' }
) => {
  const daysInCycle = differenceInDays(
    new Date(opportunityClosedAt),
    new Date(opportunityCreatedAt)
  )

  const isSameDay = isEqual(
    new Date(opportunityClosedAt),
    new Date(opportunityCreatedAt)
  )
  if (daysInCycle === 0 && isSameDay) {
    return acc + 1
  }

  return acc + Number(daysInCycle)
}

const closedWonFilter: Filter = {
  member: DRIFT_CHAT_OPPORTUNITY_OVERVIEW.OPPORTUNITY_STAGE,
  operator: CubeOperator.EQUALS,
  values: [StageStatus.CLOSED_WON],
}

const SaleCycleBaseQuery: CubeQuery = {
  name: 'SaleCycle',
  queryName: 'SaleCycle',
  query: {
    dimensions: [
      DRIFT_CHAT_OPPORTUNITY_OVERVIEW.OPPORTUNITY_CREATED_AT,
      DRIFT_CHAT_OPPORTUNITY_OVERVIEW.OPPORTUNITY_CLOSED_AT,
    ],
    filters: [closedWonFilter],
  },
}

export const useInfluencedSalesCycle = ({
  isInfluenced = false,
  isSourced = false,
}): IInfluencedSalesCycle => {
  const {
    store: { startDate, endDate },
  } = useOverviewStore()

  const kpiFilters: Filter[] = getInfluencedAndSourcedFilters({
    isInfluenced,
    isSourced,
  })

  const opportunityInfluencedSalesCycleCountCubeQuery = new CubeQueryBuilder({
    query: OpportunityCountBaseQuery,
  })
    .withName('OpportunityCount-InfluencedSalesCycleCount')
    .withQueryName('OpportunityCount-InfluencedSalesCycleCount')
    .withFilters(kpiFilters)
    .withinDateRange(
      DRIFT_CHAT_OPPORTUNITY_OVERVIEW.OPPORTUNITY_CREATED_AT,
      startDate,
      endDate
    )
    .build()

  const salesCycleInfluencedCubeQuery = new CubeQueryBuilder({
    query: SaleCycleBaseQuery,
  })
    .withOrder([[DRIFT_CHAT_OPPORTUNITY_OVERVIEW.DAYS_IN_CYCLE, 'desc']])
    .withName('SaleCycle-Influenced')
    .withQueryName('SaleCycle-Influenced')
    .withFilters(kpiFilters)
    .withinDateRange(
      DRIFT_CHAT_OPPORTUNITY_OVERVIEW.OPPORTUNITY_CREATED_AT,
      startDate,
      endDate
    )
    .build()

  const salesCycleNonInfluencedCubeQuery = new CubeQueryBuilder({
    query: SaleCycleBaseQuery,
  })
    .withName('SaleCycle-Non-Influenced')
    .withQueryName('SaleCycle-Non-Influenced')
    .withFilters([
      {
        member: DRIFT_CHAT_OPPORTUNITY_OVERVIEW.IS_INFLUENCED,
        operator: CubeOperator.EQUALS,
        values: ['false'],
      },
    ])
    .withinDateRange(
      DRIFT_CHAT_OPPORTUNITY_OVERVIEW.OPPORTUNITY_CREATED_AT,
      startDate,
      endDate
    )
    .build()

  const {
    data: resultSetInfluencedSalesCycleCount,
    isLoading: isLoadingInfluencedSalesCycleCount,
    isError: isErrorInfluencedSalesCycleCount,
  } = useCubeQuery({
    query: opportunityInfluencedSalesCycleCountCubeQuery,
  })

  const {
    data: resultSetInfluencedSalesCycle,
    isLoading: isLoadingInfluencedSalesCycle,
    isError: isErrorInfluencedSalesCycle,
  } = useCubeQuery({
    query: salesCycleInfluencedCubeQuery,
  })

  const {
    data: resultSetNonInfluenceSalesCycle,
    isLoading: isLoadingNonInfluencedSalesCycle,
    isError: isErrorNonInfluencedSalesCycle,
  } = useCubeQuery({
    query: salesCycleNonInfluencedCubeQuery,
  })

  const opportunityInfluencedSalesCycleCount = useMemo(
    () =>
      resolveCubeKPIResults(
        resultSetInfluencedSalesCycleCount,
        DRIFT_CHAT_OPPORTUNITY_OVERVIEW.COUNT ?? ''
      ) || 0,
    [resultSetInfluencedSalesCycleCount]
  )

  const averageDaysInCycleOfInfluencedOpportunities = useMemo(() => {
    const results: OpportunitiesTableRow[] = resultSetToTableData(
      resultSetInfluencedSalesCycle
    ) as OpportunitiesTableRow[]

    const sumOfDaysInCycle = results.reduce(reduceAverageDaysInCycle, 0)

    return Number(sumOfDaysInCycle / results.length) // Average of days in cycle
  }, [resultSetInfluencedSalesCycle])

  const averageDaysInCycleOfNonInfluencedOpportunities = useMemo(() => {
    const results: OpportunitiesTableRow[] = resultSetToTableData(
      resultSetNonInfluenceSalesCycle
    ) as OpportunitiesTableRow[]

    const sumOfDaysInCycle = results.reduce(reduceAverageDaysInCycle, 0)

    return Number(sumOfDaysInCycle / results.length) // Average of days in cycle
  }, [resultSetNonInfluenceSalesCycle])

  const percentageResult = useMemo(() => {
    if (!averageDaysInCycleOfNonInfluencedOpportunities) {
      return null
    }
    return (
      (Number(averageDaysInCycleOfInfluencedOpportunities) * 100) /
      Number(averageDaysInCycleOfNonInfluencedOpportunities)
    )
  }, [
    averageDaysInCycleOfInfluencedOpportunities,
    averageDaysInCycleOfNonInfluencedOpportunities,
  ])

  const isLoading = useMemo(
    () =>
      isLoadingInfluencedSalesCycleCount &&
      isLoadingInfluencedSalesCycle &&
      isLoadingNonInfluencedSalesCycle,
    [
      isLoadingInfluencedSalesCycleCount,
      isLoadingInfluencedSalesCycle,
      isLoadingNonInfluencedSalesCycle,
    ]
  )

  const hasNoData = useMemo(
    () =>
      !isLoading && !opportunityInfluencedSalesCycleCount && !percentageResult,
    [isLoading, opportunityInfluencedSalesCycleCount, percentageResult]
  )

  const hasError = useMemo(
    () =>
      !isLoading &&
      (isErrorInfluencedSalesCycleCount ||
        isErrorInfluencedSalesCycle ||
        isErrorNonInfluencedSalesCycle),
    [
      isLoading,
      isErrorInfluencedSalesCycleCount,
      isErrorInfluencedSalesCycle,
      isErrorNonInfluencedSalesCycle,
    ]
  )

  return useMemo(
    () => ({
      averageDaysInCycleOfInfluencedOpportunities,
      percentageResult: percentageResult || 0,
      hasNoData,
      hasError,
      isLoading,
      salesCycleInfluencedCubeQuery,
    }),
    [
      averageDaysInCycleOfInfluencedOpportunities,
      percentageResult,
      hasNoData,
      hasError,
      isLoading,
      salesCycleInfluencedCubeQuery,
    ]
  )
}

export default useInfluencedSalesCycle
